/** Mission Control–aligned surfaces for the case-checking shell. */

/** Subtle slow glow on interactive list rows/cards. */
export const CC_LIST_ITEM_HOVER =
  "transition-[box-shadow,background-color] duration-500 ease-out hover:bg-slate-50/40 dark:hover:bg-white/[0.012] hover:shadow-[0_0_0_1px_rgba(148,163,184,0.1),0_0_20px_rgba(148,163,184,0.05)] dark:hover:shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_0_22px_rgba(255,255,255,0.025)] focus-visible:bg-slate-50/40 dark:focus-visible:bg-white/[0.012] focus-visible:shadow-[0_0_0_1px_rgba(148,163,184,0.1),0_0_20px_rgba(148,163,184,0.05)] dark:focus-visible:shadow-[0_0_0_1px_rgba(255,255,255,0.04),0_0_22px_rgba(255,255,255,0.025)]";

/** Neutralize MissionControlCard workbench bg hover when using CC_LIST_ITEM_HOVER. */
export const CC_LIST_CARD_SHELL = `${CC_LIST_ITEM_HOVER} !overflow-visible hover:!bg-slate-50/40 focus:!bg-slate-50/40 dark:hover:!bg-white/[0.012] dark:focus:!bg-white/[0.012]`;

/** Inner layout for clickable case-checking list rows (no nested Button hover). */
export const CC_LIST_ITEM_INNER = "group flex w-full items-start gap-3 px-4 py-3 text-left";

export const CC_PANEL =
  "rounded-xl bg-white ring-1 ring-slate-200 border border-slate-200 dark:bg-white/[0.02] dark:ring-white/5 dark:border-0 p-4";
export const CC_PANEL_FLUSH =
  "rounded-xl bg-white ring-1 ring-slate-200 border border-slate-200 dark:bg-white/[0.02] dark:ring-white/5 dark:border-0 overflow-hidden";
export const CC_ROW =
  `rounded-xl bg-white ring-1 ring-slate-200 border border-slate-200 dark:bg-white/[0.02] dark:ring-white/5 dark:border-0 px-4 py-3 ${CC_LIST_ITEM_HOVER}`;
export const CC_SEARCH =
  "rounded-xl border border-slate-200 bg-white py-2.5 text-sm text-slate-900 placeholder:text-slate-400 w-full min-w-0 focus:ring-1 focus:ring-indigo-500/40 dark:border-0 dark:bg-slate-900/50 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:ring-indigo-500/30";
/** Documents toolbar — 36px outer box (border-box, 1px border included). */
export const CC_TOOLBAR_CONTROL_HEIGHT = "box-border !h-9 !min-h-9 !max-h-9";
export const CC_TOOLBAR_CONTROL_BORDER = "border border-slate-200 dark:border-white/10";
export const CC_TOOLBAR_FIELD_BORDER = "border border-slate-400 dark:border-white/20";
export const CC_TOOLBAR_GAP = "gap-2";
export const CC_TOOLBAR_SEARCH = [
  CC_TOOLBAR_CONTROL_HEIGHT,
  "w-full min-w-0 rounded-xl bg-white !py-0 text-sm leading-none text-slate-900 placeholder:text-slate-400",
  CC_TOOLBAR_FIELD_BORDER,
  "focus:ring-1 focus:ring-indigo-500/40 dark:bg-slate-900/50 dark:text-slate-200 dark:placeholder:text-slate-500 dark:focus:ring-indigo-500/30",
].join(" ");
export const CC_TOOLBAR_BUTTON = [
  CC_TOOLBAR_CONTROL_HEIGHT,
  "shrink-0 px-2.5 text-xs leading-none",
].join(" ");
export const CC_TOOLBAR_ADD_AFFORDANCE = [
  CC_TOOLBAR_CONTROL_HEIGHT,
  "inline-flex w-9 shrink-0 items-center justify-center rounded-xl text-slate-400/80 dark:text-slate-500",
  CC_TOOLBAR_CONTROL_BORDER,
].join(" ");
export const CC_TOOLBAR_SORT_GROUP = [
  CC_TOOLBAR_CONTROL_HEIGHT,
  "inline-flex shrink-0 !items-stretch !gap-0.5 !rounded-xl !bg-slate-100 !p-0.5",
  CC_TOOLBAR_FIELD_BORDER,
  "dark:!bg-slate-800",
].join(" ");
export const CC_TOOLBAR =
  "border-b border-slate-200 dark:border-white/5 pb-3 mb-3 shrink-0";
export const CC_INNER = "flex-1 min-w-0 overflow-y-auto px-6 py-4";
/** Small-caps section heading (Overview blocks, Exports, drawer sections). */
export const CC_SECTION_LABEL =
  "text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-500 mb-2";
/** Padding between underline tab rows and their panel content. */
export const CC_TAB_PANEL_PT = "pt-2";
/** Icon key at the bottom of list tabs (Rules, Checklist, Documents). */
export const CC_LEGEND =
  "flex flex-wrap items-center gap-6 text-xs text-slate-500 pt-4 border-t border-slate-200 dark:border-white/5";

/** Secondary ghost control (toolbar buttons, chips). */
export const CC_GHOST_BTN =
  "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200 transition-colors hover:bg-slate-100 hover:text-slate-900 dark:text-slate-300 dark:ring-white/10 dark:hover:bg-white/[0.04] dark:hover:text-slate-100";

/** Header row meta chips — grade, routing status (shared fixed height). */
export const CC_HEADER_META_CHIP =
  "inline-flex h-6 items-center rounded-md px-2 text-[11px] leading-none ring-1 ring-slate-200 bg-slate-100 dark:bg-white/[0.04] dark:ring-white/10";

/** Icon-only header chip — square, same height and corner radius as text chips. */
export const CC_HEADER_META_CHIP_ICON =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md p-0 ring-1 ring-slate-200 bg-slate-100 dark:bg-white/[0.04] dark:ring-white/10 box-border";

/** Muted inset surface (compare columns, document rows). */
export const CC_INSET =
  "rounded-lg bg-slate-50 ring-1 ring-slate-200 dark:bg-white/[0.02] dark:ring-white/5 p-3";

/** Form field on case-checking panels. */
export const CC_FIELD =
  "w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 dark:border-white/10 dark:bg-slate-900/50 dark:text-slate-200 dark:placeholder:text-slate-500";

/** Text link / subtle action on case-checking rows and panels. */
export const CC_ACCENT_LINK =
  "font-medium text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-200";

/** Muted underlined navigation link (missing-doc cards, upload actions). */
export const CC_SUBTLE_LINK =
  "text-xs text-slate-500 underline decoration-slate-400/60 hover:text-indigo-600 dark:hover:text-indigo-400";

/** Danger alert panel (classification failure, coverage gaps). */
export const CC_ALERT_DANGER_PANEL =
  "rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 dark:border-rose-900/40 dark:bg-rose-950/40 dark:text-rose-200";
export const CC_ALERT_DANGER_TITLE = "font-medium text-rose-900 dark:text-rose-100";
export const CC_ALERT_DANGER_BODY = "mt-1.5 leading-relaxed text-rose-800/90 dark:text-rose-200/90";
export const CC_ALERT_DANGER_ITEM =
  "rounded-lg border border-rose-200/80 bg-rose-100/50 px-3 py-2 dark:border-rose-500/20 dark:bg-rose-500/10";
export const CC_ALERT_DANGER_ITEM_TITLE = "font-medium text-rose-900 dark:text-rose-100";
export const CC_ALERT_DANGER_ITEM_BODY = "mt-1 text-xs leading-relaxed text-rose-800/90 dark:text-rose-200/90";

/** Warning alert accent (left border on panels). */
export const CC_ALERT_WARNING_TITLE = "font-medium text-amber-900 dark:text-amber-100";
export const CC_ALERT_WARNING_BODY = "text-amber-800/90 dark:text-amber-100/80";
export const CC_ALERT_WARNING_BORDER = "border-l-4 border-l-amber-500/80";
export const CC_ALERT_DANGER_BORDER = "border-l-4 border-l-rose-500/80";

/** Concerns list on amber inset panels. */
export const CC_CONCERN_TITLE =
  "font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-300";
export const CC_CONCERN_LIST =
  "list-disc text-amber-800/90 dark:text-amber-100/90 marker:text-amber-600 dark:marker:text-amber-400";

/** Success inset (“Agreed with agent”). */
export const CC_SUCCESS_INSET =
  "rounded-lg bg-emerald-50 ring-1 ring-emerald-200 p-3 dark:bg-emerald-500/10 dark:ring-emerald-500/20";
export const CC_SUCCESS_INSET_TITLE = "text-sm font-medium text-emerald-800 dark:text-emerald-100";
export const CC_SUCCESS_INSET_BODY = "mt-1 text-sm text-emerald-800/90 dark:text-emerald-200/80";

/** Inline error copy on white panels. */
export const CC_ERROR_TEXT = "text-rose-700 dark:text-rose-400";

/** Stat / count accents on list and accordion rows. */
export const CC_STAT_POSITIVE = "text-emerald-700 dark:text-emerald-400";
export const CC_STAT_NEGATIVE = "text-rose-700 dark:text-rose-400";
export const CC_STAT_ATTENTION = "text-amber-700 dark:text-amber-300";

/** Sticky table header row. */
export const CC_TABLE_HEAD = "bg-white dark:bg-slate-900/95";

/** Warning alert panel (coverage gaps). */
export const CC_ALERT_WARNING_PANEL =
  "rounded-xl border border-amber-200 bg-amber-50/80 dark:border-amber-500/30 dark:bg-amber-500/5";

/** Informational alert panel (non-warning status, e.g. review carry applied). */
export const CC_ALERT_INFO_PANEL =
  "rounded-xl border border-sky-200 bg-sky-50/90 dark:border-sky-500/25 dark:bg-sky-500/[0.07]";
export const CC_ALERT_INFO_TITLE = "font-medium text-sky-900 dark:text-sky-100";
export const CC_ALERT_INFO_BODY = "text-sky-800/95 dark:text-sky-100/90";
export const CC_ALERT_INFO_BORDER = "border-l-4 border-l-sky-500/80";

export const CC_LABEL_DISPUTED = "text-amber-700 dark:text-amber-300";
export const CC_LABEL_FAILED = "text-rose-700 dark:text-rose-300";

/** Aliases — prefer {@link CC_CONCERN_TITLE} / {@link CC_CONCERN_LIST}. */
export const CC_CONCERNS_HEADING = CC_CONCERN_TITLE;
export const CC_CONCERNS_LIST = CC_CONCERN_LIST;
